import wrappedLocalStorage from 'flow/utils/local-storage-utils';

const cookieName = 'webapp-accepted-cookies';
const categoriesCookieName = 'webapp-cookie-categories';
const oneYear = 60 * 60 * 24 * 365 * 1000;

// localStorage fallback keys, used only when a cookie write doesn't actually
// persist (cookies blocked, or a non-persistent Electron session partition —
// AC-1530 made the consent prompt appear on desktop too). Without this,
// consent never round-trips and the prompt reappears on every boot even
// after the user has chosen accept/reject.
const fallbackKeyFor = {
    [cookieName]: 'webapp-accepted-cookies-fallback',
    [categoriesCookieName]: 'webapp-cookie-categories-fallback',
};

let loggedCookieWriteFailure = false;

const readCookie = (name) => {
    const cookies = '; ' + document.cookie;

    if (cookies.indexOf('; ' + name + '=') !== -1) {
        return decodeURIComponent(
            (cookies.split('; ' + name + '=')[1] || '').split(';')[0],
        );
    } else {
        return null;
    }
};

const readCookieWithFallback = (name) => {
    const value = readCookie(name);
    if (value !== null) return value;

    const fallbackKey = fallbackKeyFor[name];
    return fallbackKey ? wrappedLocalStorage.getItem(fallbackKey) : null;
};

const writeCookie = (name, value, date) => {
    document.cookie =
        name +
        '=' +
        encodeURIComponent(value) +
        '; expires=' +
        new Date(date).toUTCString() +
        '; path=/' +
        '; Secure; SameSite=Lax';

    const fallbackKey = fallbackKeyFor[name];
    if (!fallbackKey) return;

    // A blocked or non-persistent cookie store doesn't throw on write — the
    // assignment just silently does nothing — so verify by reading back
    // rather than trusting it succeeded.
    if (readCookie(name) === value) {
        wrappedLocalStorage.removeItem(fallbackKey);
        return;
    }

    if (!loggedCookieWriteFailure) {
        loggedCookieWriteFailure = true;
        console.warn(
            `Cookie write for '${name}' did not persist (cookies blocked, or a non-persistent session) — falling back to localStorage so consent state survives a restart.`,
        );
    }
    wrappedLocalStorage.setItem(fallbackKey, value);
};

const clearCookie = (name) => {
    document.cookie =
        name +
        '=; expires=' +
        new Date(0).toUTCString() +
        '; path=/' +
        '; Secure; SameSite=Lax';

    const fallbackKey = fallbackKeyFor[name];
    if (fallbackKey) wrappedLocalStorage.removeItem(fallbackKey);
};

export const hasAcceptedCookies = () => {
    if (typeof window === 'undefined') return false;
    const val = readCookieWithFallback(cookieName);
    return val === 'accepted' || val === 'true';
};

export const setAcceptedCookies = () => {
    return writeCookie(cookieName, 'accepted', +new Date() + oneYear);
};

export const hasRejectedCookies = () => {
    return (
        typeof window !== 'undefined' &&
        readCookieWithFallback(cookieName) === 'rejected'
    );
};

export const setRejectedCookies = () => {
    return writeCookie(cookieName, 'rejected', +new Date() + oneYear);
};

export const hasPartialCookieConsent = () => {
    return (
        typeof window !== 'undefined' &&
        readCookieWithFallback(cookieName) === 'partial'
    );
};

export const setPartialCookieConsent = () => {
    return writeCookie(cookieName, 'partial', +new Date() + oneYear);
};

export const getConsentCategories = () => {
    if (typeof window === 'undefined') return null;
    const raw = readCookieWithFallback(categoriesCookieName);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const setConsentCategories = (categories) => {
    return writeCookie(
        categoriesCookieName,
        JSON.stringify(categories),
        +new Date() + oneYear,
    );
};

export const clearConsentCookies = () => {
    clearCookie(cookieName);
    clearCookie(categoriesCookieName);
};
